// ═══════════════════════════════════════════════════════════════════════════
// episodeAudio.js — generate an episode's narration MP3 exactly once, then
// reuse it forever.
//
// Resolution order (cheapest first) — we NEVER call OpenAI if a URL exists:
//   1. In-memory cache (this session)
//   2. episodes.audio_url already stamped on the row
//   3. A fresh DB read (another device/session may have generated it)
//   4. Generate via /api/tts (OpenAI TTS-1, capped ~4000 chars), upload the
//      MP3 to the `episode-audio` bucket, and stamp episodes.audio_url.
//
// If Storage/DB writes fail (bucket/migration 069 not applied yet), we fall
// back to a session-only object URL so playback still works — but that path
// will regenerate next session, so run migration 069 to make it permanent.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';
import { extractEpisodeScript } from './episodeScript';

const memCache = new Map(); // episodeId -> playable url

/** Returns a playable MP3 URL for the episode, generating + caching it once. */
export async function getEpisodeAudioUrl(ep, { voice = 'nova', userId } = {}) {
  if (!ep?.id) throw new Error('Missing episode.');

  // 1. Session cache.
  if (memCache.has(ep.id)) return memCache.get(ep.id);

  // 2. Already stamped on the episode we were handed.
  const stamped = ep.audio_url || ep.result?.audio_url;
  if (stamped) { memCache.set(ep.id, stamped); return stamped; }

  // 3. Fresh DB read — someone may have generated it elsewhere.
  if (supabase) {
    try {
      const { data } = await supabase
        .from('episodes')
        .select('audio_url')
        .eq('id', ep.id)
        .maybeSingle();
      if (data?.audio_url) { memCache.set(ep.id, data.audio_url); return data.audio_url; }
    } catch { /* column missing (pre-069) → fall through to generate */ }
  }

  // 4. Generate once.
  const text = extractEpisodeScript(ep.result);
  if (!text) throw new Error('Nothing to narrate yet — try regenerating the episode.');

  let res;
  try {
    res = await fetch('/api/tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, voice }),
    });
  } catch (e) {
    throw new Error('Network error reaching narration. Check your connection and retry.');
  }
  if (!res.ok) {
    if (res.status === 503) throw new Error('Narration isn’t enabled yet (OPENAI_API_KEY not set in Vercel).');
    let detail = '';
    try { detail = (await res.json())?.error || ''; } catch { detail = await res.text().catch(() => ''); }
    throw new Error(String(detail || `Narration failed (${res.status}).`).slice(0, 200));
  }
  const blob = await res.blob();

  // Upload + stamp so it's never regenerated. On any failure, fall back to a
  // session-only object URL (still plays; regenerates next session).
  let url = null;
  if (supabase && userId) {
    const path = `${userId}/${ep.id}.mp3`;
    try {
      const { error: upErr } = await supabase.storage
        .from('episode-audio')
        .upload(path, blob, { upsert: true, contentType: 'audio/mpeg' });
      if (!upErr) {
        url = supabase.storage.from('episode-audio').getPublicUrl(path).data.publicUrl;
        // Fire-and-forget stamp — a failure here just means we re-read/regen
        // later; it must never block playback.
        supabase
          .from('episodes')
          .update({ audio_url: url, audio_voice: voice })
          .eq('id', ep.id)
          .eq('user_id', userId)
          .then(() => {}, () => {});
      } else {
        console.warn('[episodeAudio] storage upload failed (run migration 069):', upErr.message);
      }
    } catch (e) {
      console.warn('[episodeAudio] storage error (run migration 069):', e?.message || e);
    }
  }
  if (!url) url = URL.createObjectURL(blob);

  memCache.set(ep.id, url);
  return url;
}

/** True once we have (or can cheaply resolve) audio for this episode without
 *  hitting OpenAI — used to label the export as free vs first-generate. */
export function hasCachedAudio(ep) {
  return !!(ep && (memCache.has(ep.id) || ep.audio_url || ep.result?.audio_url));
}
